'use client';

import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

import { W, T, FONT, LAYOUT } from './tokens';

/**
 * Header for the marketing site, and the button styles the page shares.
 *
 * The hero itself lives in hero/HomeHero.tsx.
 *
 * The mark is drawn here rather than imported from components/design/FitventMark
 * for one reason: that component resolves its two fills from `--fvd-mark-*`,
 * which follow the APP's theme. This page is its own dark ground regardless of
 * what theme a visitor has set, so it needs the fills stated outright. The
 * geometry is identical and comes from the same brand pack, so the two cannot
 * diverge in shape, only in colour, which is the point.
 */
export function WebsiteMark({ size = 26, accent, body }: { size?: number; accent: string; body: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Fitvent" style={{ flex: 'none', display: 'block' }}>
      <polygon points="8,14 92,14 92,28 22,28" fill={accent} />
      <polygon points="34,44 92,44 92,58 48,58" fill={body} />
      <polygon points="68,74 92,74 92,88 78,88" fill={body} />
    </svg>
  );
}

const NAV = [
  { label: 'Platform', href: '#platform' },
  { label: 'Event types', href: '#eventtypes' },
  { label: 'Pricing', href: '#pricing' },
];

export function WebsiteHeader() {
  // A wrapped row of five links centred under the wordmark read as a page that
  // had run out of room. Four destinations behind a button is less chrome than
  // four destinations spilling onto two lines.
  const [open, setOpen] = useState(false);
  // The lockup is sized for a 1300px header. Next to a 44px menu button on a
  // 390px screen it was the loudest thing on the page.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header
      className="fvw-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        padding: LAYOUT.headerPadding,
        // No rule under the header. A border here cut a hard line straight
        // across the hero's glow and made the header read as a separate bar
        // bolted on top. The glow now washes up behind it (see WebsiteHome),
        // and the header is simply where the hero starts.
        position: 'relative',
      }}
    >
      <a
        href="#top"
        style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 8 : 10, textDecoration: 'none' }}
      >
        <WebsiteMark size={isMobile ? 19 : 23} accent={W.ice} body={W.bone} />
        {/* The wordmark is always lowercase; only prose capitalises it. */}
        <span style={{ fontSize: isMobile ? 21 : 25, fontWeight: 700, letterSpacing: '-0.04em', color: W.bone }}>
          fitvent
        </span>
      </a>

      <button
        type="button"
        className="fvw-burger"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
      </button>

      <nav className={open ? 'fvw-nav fvw-nav-open' : 'fvw-nav'} style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
        {NAV.map((item) => (
          <a
            key={item.href}
            href={item.href}
            style={{ ...T.nav, color: W.bone, textDecoration: 'none' }}
            className="fvw-navlink"
            onClick={() => setOpen(false)}
          >
            {item.label}
          </a>
        ))}
        <a href="/sign-in" style={{ ...T.nav, color: W.bone, textDecoration: 'none' }} className="fvw-navlink">
          Sign in
        </a>
        <a href="/sign-up" style={primaryButton} className="fvw-cta">
          Get started
        </a>
      </nav>
    </header>
  );
}

export const primaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: W.ice,
  color: W.obsidian,
  fontFamily: FONT,
  fontSize: 15,
  fontWeight: 600,
  padding: '11px 20px',
  borderRadius: 6,
  border: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const secondaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: W.bone,
  fontFamily: FONT,
  fontSize: 15,
  fontWeight: 400,
  padding: '11px 20px',
  borderRadius: 6,
  border: `1px solid ${W.darkBorder}`,
  textDecoration: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
